/**
 * useAuth — PALACIO DA SABEDORIA
 *
 * Sistema de autenticação 100% próprio.
 * Não usa Lumi Auth. Não precisa de OTP.
 * Guarda o token em localStorage e verifica-o contra a BD via Deno Function.
 * Expõe modulePermissions para controlo granular de acesso por módulo e ação.
 */
import { useEffect, useState, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import type { ModulePermissions, UserModuleAccess, ModulePermission } from '@/types'

export interface AuthUser {
  id:                string
  userId:            string
  username:          string
  displayName:       string
  permission:        string
  email:             string
  modulePermissions: ModulePermissions
  modules:           UserModuleAccess[]
}

const TOKEN_KEY = 'rcf_session_token'

// ─── Funções utilitárias de sessão ────────────────────────────────────────────

export function getStoredToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY) } catch { return null }
}

export function storeToken(token: string): void {
  try { localStorage.setItem(TOKEN_KEY, token) } catch { /* ignorado */ }
}

export function clearToken(): void {
  try { localStorage.removeItem(TOKEN_KEY) } catch { /* ignorado */ }
}

// ─── Login: chama a Deno Function com action="login" ─────────────────────────

export async function signIn(username: string, password: string): Promise<AuthUser> {
  const result = await lumi.functions.invoke('createAdminUser', {
    method: 'POST',
    body: { action: 'login', username: username.trim(), password },
  }) as any

  if (!result?.success) {
    throw new Error(result?.error ?? 'Utilizador ou senha incorretos.')
  }

  storeToken(result.token)
  return {
    ...result.user,
    modulePermissions: result.user?.modulePermissions ?? {},
    modules:           result.user?.modules           ?? [],
  } as AuthUser
}

// ─── Logout ──────────────────────────────────────────────────────────────────

export function signOut(): void {
  clearToken()
  window.location.href = '/admin/login'
}

// ─── Helpers de permissão (exportados para uso nas páginas) ──────────────────

/** Verifica se o utilizador tem acesso VIEW num módulo */
export function canView(user: AuthUser | null, mod: UserModuleAccess): boolean {
  if (!user) return false
  if (user.permission === 'admin') return true
  return !!(user.modulePermissions?.[mod]?.view)
}

/** Verifica se o utilizador tem acesso EDIT num módulo */
export function canEdit(user: AuthUser | null, mod: UserModuleAccess): boolean {
  if (!user) return false
  if (user.permission === 'admin') return true
  return !!(user.modulePermissions?.[mod]?.edit)
}

/** Verifica se o utilizador tem acesso DELETE num módulo */
export function canDelete(user: AuthUser | null, mod: UserModuleAccess): boolean {
  if (!user) return false
  if (user.permission === 'admin') return true
  return !!(user.modulePermissions?.[mod]?.delete)
}

/** Verifica se o utilizador tem QUALQUER acesso num módulo */
export function canAccess(user: AuthUser | null, mod: UserModuleAccess): boolean {
  if (!user) return false
  if (user.permission === 'admin') return true
  const p: ModulePermission | undefined = user.modulePermissions?.[mod]
  return !!(p?.view || p?.edit || p?.delete)
}

// ─── Hook principal ───────────────────────────────────────────────────────────

export function useAuth() {
  const [isLoading,       setIsLoading]       = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user,            setUser]            = useState<AuthUser | null>(null)

  const isAdmin = user?.permission === 'admin'

  const verifySession = useCallback(async () => {
    const token = getStoredToken()
    if (!token) { setIsLoading(false); return }

    try {
      const result = await lumi.functions.invoke('createAdminUser', {
        method: 'POST',
        body: { action: 'verify', token },
      }) as any

      if (result?.valid && result?.user) {
        setUser({
          ...result.user,
          modulePermissions: result.user?.modulePermissions ?? {},
          modules:           result.user?.modules           ?? [],
        } as AuthUser)
        setIsAuthenticated(true)
      } else {
        clearToken()
        setIsAuthenticated(false)
        setUser(null)
      }
    } catch {
      setIsAuthenticated(false)
      setUser(null)
      clearToken()
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    verifySession()
  }, [verifySession])

  return { user, isAuthenticated, isLoading, isAdmin }
}
